/**
 * Как называть устройство, которое назвал телефон.
 *
 * <p>Браузер отдаёт подпись устройства такой, какой её придумала система, и на телефонах она
 * читается как поломка (прогон 01.10.2026): iPhone перечисляет каждый объектив отдельной
 * камерой – «Back Triple Camera», «Back Dual Wide Camera», «Back Ultra Wide Camera», – выход
 * звука называет на языке системы («Динамик», «Приемник», «По умолчанию - AirPods») поверх
 * английского интерфейса, а Android называет встроенный микрофон маршрутом звонка –
 * «Speakerphone», «Headset earpiece», камеры – «camera2 1, facing front».
 *
 * <p>Здесь только распознавание: что это за устройство по смыслу. Слова подставляет тот, кто
 * рисует список, – из своих переводов. Нераспознанная подпись остаётся как есть: имя гарнитуры
 * или вебкамеры человек узнаёт сам, и переименовать её значило бы спрятать.
 */

export type DeviceLabelKind = 'audioinput' | 'audiooutput' | 'videoinput';

/** Устройство по смыслу. `null` – подпись своя, её показываем как есть. */
export type DeviceRole =
  | { role: 'frontCamera' }
  | { role: 'backCamera' }
  /** Громкая связь: iPhone «Динамик», Android «Speakerphone». */
  | { role: 'speaker' }
  /** Телефон у уха: iPhone «Приемник», Android «Headset earpiece». */
  | { role: 'earpiece' }
  | { role: 'wiredHeadset' }
  | { role: 'bluetoothHeadset' }
  | { role: 'usbAudio' }
  /** «По умолчанию - AirPods»: системный выбор и то, что за ним сейчас стоит. */
  | { role: 'systemDefault'; name: string };

export type CameraFacing = 'front' | 'back';

/** Сторона камеры по подписи iPhone, Android и русской системы. */
export const cameraFacing = (label: string): CameraFacing | null => {
  if (/\bfront\b|facing front|передн|фронтальн/i.test(label)) {
    return 'front';
  }
  if (/\bback\b|\brear\b|facing back|задн|основн/i.test(label)) {
    return 'back';
  }
  return null;
};

/**
 * Объектив сверх основного: широкий, телевик, сдвоенный. Для звонка это одна и та же задняя
 * камера, и выбирать между ними человеку незачем.
 */
const EXTRA_LENS = /triple|dual|ultra|tele|wide|macro|depth|тройн|двойн|сверхшир|телефото/i;

const DEFAULT_PREFIX = /^(?:default|по умолчанию)\s*[-–—:]\s*(.+)$/i;

export const describeDevice = (kind: DeviceLabelKind, label: string): DeviceRole | null => {
  const text = label.trim();
  if (!text) {
    return null;
  }

  if (kind === 'videoinput') {
    const facing = cameraFacing(text);
    return facing === 'front'
      ? { role: 'frontCamera' }
      : facing === 'back'
        ? { role: 'backCamera' }
        : null;
  }

  const fallback = DEFAULT_PREFIX.exec(text);
  if (fallback?.[1]) {
    return { role: 'systemDefault', name: fallback[1].trim() };
  }

  if (/^(?:wired headset|проводная гарнитура)$/i.test(text)) {
    return { role: 'wiredHeadset' };
  }
  if (/^(?:bluetooth headset|bluetooth-гарнитура)$/i.test(text)) {
    return { role: 'bluetoothHeadset' };
  }
  if (/^usb audio$/i.test(text)) {
    return { role: 'usbAudio' };
  }

  /*
    Android называет встроенный микрофон маршрутом звонка – громкая связь или у уха, – и выбор
    между ними переключает заодно и выход звука. Поэтому это две разные строки, а не один
    «микрофон телефона».
  */
  if (/^(?:speakerphone|speaker|динамик)$/i.test(text)) {
    return { role: 'speaker' };
  }
  if (/^(?:headset earpiece|earpiece|receiver|приемник|приёмник)$/i.test(text)) {
    return { role: 'earpiece' };
  }
  return null;
};

type CameraOption = { deviceId: string; label: string };

/**
 * Камеры телефона – две: фронтальная и основная.
 *
 * <p>Из задних берём ту, что уже выбрана, иначе обычную (без «широкой», «тройной» и прочих
 * объективов в подписи), иначе первую. Нераспознанные камеры – вебкамера, камера iPhone у Mac –
 * остаются все: их подписи и так понятны, а сторон у них нет.
 */
export const pickPhoneCameras = <T extends CameraOption>(
  cameras: readonly T[],
  activeId?: string | null
): T[] => {
  const fronts = cameras.filter((camera) => cameraFacing(camera.label) === 'front');
  const backs = cameras.filter((camera) => cameraFacing(camera.label) === 'back');

  if (fronts.length + backs.length === 0) {
    return [...cameras];
  }

  const choose = (group: T[]) =>
    group.find((camera) => camera.deviceId === activeId) ??
    group.find((camera) => !EXTRA_LENS.test(camera.label)) ??
    group[0];

  const front = choose(fronts);
  const back = choose(backs);

  return cameras.filter((camera) => {
    const facing = cameraFacing(camera.label);
    if (facing === 'front') {
      return camera === front;
    }
    if (facing === 'back') {
      return camera === back;
    }
    return true;
  });
};

/**
 * «По умолчанию - AirPods» рядом с «AirPods» – одно и то же устройство дважды. На телефоне
 * системную запись убираем, когда то, что за ней стоит, уже есть в списке своей строкой. На
 * десктопе она значит другое – «следовать за системой, когда я сменю наушники», – поэтому решает
 * вызывающий.
 */
export const dropDuplicateDefault = <T extends { label: string }>(devices: readonly T[]): T[] => {
  const names = new Set(devices.map((device) => device.label.trim().toLowerCase()));

  return devices.filter((device) => {
    const fallback = DEFAULT_PREFIX.exec(device.label.trim());
    return !(fallback?.[1] && names.has(fallback[1].trim().toLowerCase()));
  });
};
